import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { IconSelector } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";

export interface SelectOption {
  value: string;
  label: string;
}

/** Dropdown list height cap (~7 rows) before the ScrollArea takes over. */
const LIST_MAX_HEIGHT = 224;

interface Props {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Minimal custom select used inside the date popover. Its dropdown renders
 * inline (no portal) so clicks stay within the popover's DOM subtree and don't
 * trip the popover's outside-press dismiss.
 */
export const OptionSelect = ({
  value,
  options,
  onChange,
  placeholder,
  className,
}: Props) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDown, true);
    return () => document.removeEventListener("mousedown", handleDown, true);
  }, [open]);

  const selected = options.find((option) => option.value === value);

  return (
    <div ref={ref} className={cn("tw-relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-px-3 tw-text-sm tw-text-blue tw-outline-none"
      >
        <span
          className={cn(
            "tw-flex-1 tw-truncate tw-text-left",
            !selected && "tw-text-blue-500",
          )}
        >
          {selected ? selected.label : (placeholder ?? "")}
        </span>
        <IconSelector
          size={16}
          stroke={1.5}
          className="tw-shrink-0 tw-text-blue-500"
        />
      </button>
      {open && (
        <div className="tw-absolute tw-left-0 tw-top-[calc(100%+4px)] tw-z-10 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-p-1 tw-shadow-sm">
          <ScrollArea maxHeight={LIST_MAX_HEIGHT}>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={cn(
                  "tw-bg-transparent tw-flex tw-h-8 tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-border-none tw-px-2 tw-text-left tw-text-sm tw-text-blue tw-transition-colors hover:tw-bg-blue-50",
                  option.value === value && "tw-bg-blue-50",
                )}
              >
                {option.label}
              </button>
            ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
